import { useEffect, useMemo, useRef, useState } from "react";
import {
	Badge,
	Button,
	Card,
	Container,
	Form,
	InputGroup,
	Row,
	Col,
	Spinner,
	Table,
} from "react-bootstrap";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { getRiders } from "../../services/riderService";
import { Rider } from "../../types/model";
import AthleteProfileModal from "./AthleteProfileModal";
import "./RidersPage.css";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	fetchSeriesThunk,
	fetchSportsThunk,
} from "../../store/thunks/fantasyThunk";

interface RiderAvatarProps {
	imageUrl?: string;
	riderName: string;
}

const RiderAvatar = ({ imageUrl, riderName }: RiderAvatarProps) => {
	const [hasImageError, setHasImageError] = useState(false);
	const normalizedImageUrl = imageUrl?.trim() ?? "";
	const showImage = normalizedImageUrl.length > 0 && !hasImageError;

	if (showImage) {
		return (
			<img
				src={normalizedImageUrl}
				alt={`${riderName} avatar`}
				className="riders-page__avatar"
				onError={() => setHasImageError(true)}
			/>
		);
	}

	return <span className="riders-page__avatar riders-page__avatar--empty" />;
};

const RidersPage = () => {
	const dispatch = useAppDispatch();
	const [riders, setRiders] = useState<Rider[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
	const [showQuickView, setShowQuickView] = useState(false);
	const { sports, series, selectedSport, selectedSeries } = useAppSelector(
		(state: RootState) => state.fantasy,
	);
	const [selectedSportId, setSelectedSportId] = useState(() => {
		if (selectedSport?._id) {
			return selectedSport._id;
		}
		if (typeof window === "undefined") {
			return "";
		}
		return window.localStorage.getItem("ridersPage:selectedSportId") || "";
	});
	const [selectedSeriesId, setSelectedSeriesId] = useState(() => {
		if (selectedSeries?._id) {
			return selectedSeries._id;
		}
		if (typeof window === "undefined") {
			return "";
		}
		return window.localStorage.getItem("ridersPage:selectedSeriesId") || "";
	});
	const hasUserChangedSportFilterRef = useRef(false);
	const hasUserChangedSeriesFilterRef = useRef(false);

	useEffect(() => {
		if (sports.length === 0) {
			dispatch(fetchSportsThunk());
		}
		if (series.length === 0) {
			dispatch(fetchSeriesThunk());
		}
	}, [dispatch, series.length, sports.length]);

	useEffect(() => {
		let isCancelled = false;

		const fetchRiders = async () => {
			setIsLoading(true);
			setError("");
			try {
				const res = await getRiders();
				if (isCancelled) {
					return;
				}
				setRiders(Array.isArray(res?.data) ? res.data : []);
			} catch (fetchError) {
				if (isCancelled) {
					return;
				}
				setError("Unable to load riders right now.");
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		fetchRiders();

		return () => {
			isCancelled = true;
		};
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		window.localStorage.setItem("ridersPage:selectedSportId", selectedSportId);
	}, [selectedSportId]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		window.localStorage.setItem("ridersPage:selectedSeriesId", selectedSeriesId);
	}, [selectedSeriesId]);

	useEffect(() => {
		if (
			!hasUserChangedSportFilterRef.current &&
			selectedSport?._id &&
			selectedSportId !== selectedSport._id
		) {
			setSelectedSportId(selectedSport._id);
		}
	}, [selectedSport?._id, selectedSportId]);

	useEffect(() => {
		if (
			!hasUserChangedSeriesFilterRef.current &&
			selectedSeries?._id &&
			selectedSeriesId !== selectedSeries._id
		) {
			setSelectedSeriesId(selectedSeries._id);
		}
	}, [selectedSeries?._id, selectedSeriesId]);

	const filteredRiders = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();
		const sorted = [...riders].sort((a, b) => {
			const lastNameCompare = (a.last_name || "").localeCompare(b.last_name || "");
			if (lastNameCompare !== 0) {
				return lastNameCompare;
			}
			return (a.first_name || "").localeCompare(b.first_name || "");
		});

		return sorted.filter((rider) => {
			const isActive = (rider.status || "").trim().toLowerCase() === "active";
			if (!isActive) {
				return false;
			}

			const matchesSport = selectedSportId
				? (rider.sports || []).includes(selectedSportId)
				: true;
			const matchesSeries = selectedSeriesId
				? (rider.series || []).includes(selectedSeriesId)
				: true;

			const fullName = `${rider.first_name || ""} ${rider.last_name || ""}`
				.trim()
				.toLowerCase();
			const matchesSearch =
				!normalizedSearch ||
				fullName.includes(normalizedSearch) ||
				(rider.country || "").toLowerCase().includes(normalizedSearch) ||
				(rider.team || "").toLowerCase().includes(normalizedSearch);

			return matchesSport && matchesSeries && matchesSearch;
		});
	}, [riders, searchTerm, selectedSeriesId, selectedSportId]);

	const openQuickView = (rider: Rider) => {
		setSelectedRider(rider);
		setShowQuickView(true);
	};

	return (
		<Container className="riders-page py-4" style={{ minHeight: "100vh" }}>
			<div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3">
				<div>
					<h2 className="mb-1">Riders</h2>
					<div className="riders-page__subtitle">
						Browse athlete profiles and past results.
					</div>
				</div>
				<Badge className="riders-page__count-badge">{filteredRiders.length} riders</Badge>
			</div>

			<InputGroup className="riders-page__search mb-3">
				<Form.Control
					className="riders-page__search-input"
					placeholder="Search by rider, country, or team"
					value={searchTerm}
					onChange={(event) => setSearchTerm(event.target.value)}
				/>
				<Button
					variant="outline-light"
					onClick={() => setSearchTerm("")}
					disabled={!searchTerm}
				>
					Clear
				</Button>
			</InputGroup>
			<Row className="g-2 mb-3">
				<Col md={6}>
					<Form.Select
						className="riders-page__select"
						value={selectedSportId}
						onChange={(event) => {
							hasUserChangedSportFilterRef.current = true;
							setSelectedSportId(event.target.value);
						}}
					>
						<option value="">All Sports</option>
						{sports.map((sport) => (
							<option key={sport._id} value={sport._id}>
								{sport.shortName || sport.name}
							</option>
						))}
					</Form.Select>
				</Col>
				<Col md={6}>
					<Form.Select
						className="riders-page__select"
						value={selectedSeriesId}
						onChange={(event) => {
							hasUserChangedSeriesFilterRef.current = true;
							setSelectedSeriesId(event.target.value);
						}}
					>
						<option value="">All Series</option>
						{series.map((seriesItem) => (
							<option key={seriesItem._id} value={seriesItem._id}>
								{seriesItem.name}
							</option>
						))}
					</Form.Select>
				</Col>
			</Row>

			{isLoading && (
				<div className="d-flex justify-content-center py-4">
					<Spinner animation="border" role="status" />
				</div>
			)}

			{error && !isLoading && (
				<Card className="riders-page__notice riders-page__notice--error p-3">
					{error}
				</Card>
			)}

			{!isLoading && !error && filteredRiders.length === 0 && (
				<Card className="riders-page__notice p-3">
					No riders match your search.
				</Card>
			)}

			{!isLoading && !error && filteredRiders.length > 0 && (
				<div className="riders-page__table-wrap">
					<Table className="riders-page__table" striped hover responsive>
						<thead>
							<tr>
								<th>Country</th>
								<th>Gender</th>
								<th>Rider</th>
								<th>Team</th>
								<th className="text-end">Profile</th>
							</tr>
						</thead>
						<tbody>
							{filteredRiders.map((rider) => {
								const riderName = `${rider.first_name} ${rider.last_name}`;
								const countryCode = getCountryISO2(
									(rider.country || "").toUpperCase().trim(),
								);

								return (
									<tr key={rider._id || riderName}>
										<td>
											<div className="d-flex align-items-center gap-2">
												{countryCode ? (
													<ReactCountryFlag
														countryCode={countryCode}
														svg
														style={{ width: "1.5rem", height: "1.1rem" }}
													/>
												) : null}
												<span>{rider.country || "N/A"}</span>
											</div>
										</td>
										<td>
											<Badge className="riders-page__gender-badge">
												{rider.gender || "-"}
											</Badge>
										</td>
										<td>
											<div className="riders-page__rider-cell">
												<RiderAvatar
													imageUrl={rider.imageUrl}
													riderName={riderName}
												/>
												<span className="fw-semibold">{riderName}</span>
											</div>
										</td>
										<td>{rider.team || "No team"}</td>
										<td>
											<div className="d-flex justify-content-end gap-2">
												<Button
													variant="outline-info"
													size="sm"
													onClick={() => openQuickView(rider)}
													disabled={!rider._id}
												>
													Athlete Profile
												</Button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			)}

			<AthleteProfileModal
				show={showQuickView}
				onHide={() => setShowQuickView(false)}
				riderId={selectedRider?._id}
				riderName={
					selectedRider
						? `${selectedRider.first_name} ${selectedRider.last_name}`
						: undefined
				}
				initialRider={selectedRider}
			/>
		</Container>
	);
};

export default RidersPage;
